import React, { useEffect, useState, Suspense, lazy } from "react";
import { pillars, showcase } from "../../data/mock";
import api from "../../lib/api";
import StaticGlobe from "./StaticGlobe";
import { isLowPowerClient } from "../../lib/platform";

// Heavy WebGL globe — loaded only on desktop, so phones never pay the three.js cost.
const Globe3D = lazy(() => import("../three/Globe3D"));

export function Manifesto() {
  return (
    <section id="mission" className="relative mx-auto max-w-6xl px-5 py-28 lg:px-8">
      <div className="mx-auto max-w-3xl text-center">
        <p className="eyebrow">The win</p>
        <h2 className="mt-5 text-3xl font-semibold leading-tight text-mist sm:text-5xl">
          Winning isn't a moment. It's a habit.
        </h2>
        <p className="mt-6 text-lg leading-relaxed text-mist-muted">
          Every check-in is a win. Every meal logged is a win. Every workout finished — whether it was
          perfect or brutal — is a win. WYVN meets you at the win: AI coaches who speak your language,
          trackers that see your progress, quests that make the daily feel extraordinary. You don't have
          to be perfect. You just have to win today — one small win at a time.
        </p>
      </div>

      <div className="mt-16 grid gap-6 md:grid-cols-3">
        {pillars.map((p) => (
          <div
            key={p.key}
            className="slate-card group rounded-3xl p-8"
          >
            <div className="mb-5 inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-brand/15 font-display text-lg font-bold text-brand-soft">
              {p.title.charAt(0)}
            </div>
            <h3 className="text-xl font-semibold text-mist">{p.title}</h3>
            <p className="mt-3 text-sm leading-relaxed text-mist-muted">{p.body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

export function MemberMap() {
  const [map, setMap] = useState(null);
  const lite = isLowPowerClient();
  useEffect(() => { api.publicMap().then(setMap).catch(() => {}); }, []);
  const hasReal = map && map.points && map.points.length > 0;
  return (
    <section id="map" className="relative mx-auto max-w-6xl px-5 py-16 lg:px-8">
      <div className="mb-10 text-center">
        <p className="eyebrow">The weave</p>
        <h2 className="mt-5 text-3xl font-semibold text-mist sm:text-4xl">
          No one wins alone.
        </h2>
        {hasReal && (
          <p className="mt-4 text-sm text-mist-muted" data-testid="member-map-caption">
            <span className="font-semibold text-mist">{map.members.toLocaleString()}</span> {map.members === 1 ? "member" : "members"} across{" "}
            <span className="font-semibold text-mist">{map.countries}</span> {map.countries === 1 ? "country" : "countries"} — live.
          </p>
        )}
      </div>

      <div className="glass relative overflow-hidden rounded-[2rem] p-4 sm:p-6">
        <div className="pointer-events-none absolute inset-0 grid-lines opacity-40" />
        <div className="pointer-events-none absolute left-1/2 top-1/2 h-[460px] w-[860px] -translate-x-1/2 -translate-y-1/2 rounded-[50%] bg-brand/12 blur-[100px]" />
        <div className="relative mx-auto w-full max-w-4xl" data-testid="member-globe">
          {lite ? (
            <StaticGlobe height={440} />
          ) : (
            <Suspense fallback={<StaticGlobe height={480} />}>
              <Globe3D height={480} interactive points={map?.points} arcs={map?.arcs} />
            </Suspense>
          )}
        </div>
        <div className="mt-2 text-center text-sm text-mist-muted">
          {hasReal ? "Real members, moving worldwide" : "Members moving worldwide"}
        </div>
      </div>
    </section>
  );
}

export function AppShowcase() {
  return (
    <section id="app" className="relative mx-auto max-w-6xl px-5 py-28 lg:px-8">
      <div className="mb-14 text-center">
        <p className="eyebrow">Inside the app</p>
        <h2 className="mt-5 text-3xl font-semibold text-mist sm:text-5xl">
          A complete wellness operating system
        </h2>
        <p className="mx-auto mt-5 max-w-2xl text-mist-muted">
          Not a tracker. A calm, intelligent home for your whole life — training, food, mind,
          cycle and the people who move with you.
        </p>
      </div>

      <div className="space-y-8">
        {showcase.map((s, i) => (
          <div
            key={s.title}
            className={`glass grid items-center gap-8 overflow-hidden rounded-[2rem] p-6 md:grid-cols-2 md:p-8 ${
              i % 2 === 1 ? "md:[&>*:first-child]:order-2" : ""
            }`}
          >
            <div className="relative h-64 overflow-hidden rounded-2xl md:h-80">
              <img src={s.image} alt={s.title} className="h-full w-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent" />
            </div>
            <div className="px-2 md:px-6">
              <p className="eyebrow">{s.label}</p>
              <h3 className="mt-3 text-2xl font-semibold text-mist sm:text-3xl">{s.title}</h3>
              <p className="mt-4 leading-relaxed text-mist-muted">{s.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
